import React, { useState, useEffect, useCallback } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import {
  checklistService,
  ChecklistItem as ServiceChecklistItem,
} from '../services/checklistService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  CheckSquare,
  Clock,
  Camera,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Calendar,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format } from 'date-fns';

interface ChecklistItem {
  id: string;
  title: string;
  description?: string;
  category:
    | 'opening'
    | 'closing'
    | 'maintenance'
    | 'customer_service'
    | 'custom';
  priority: 'low' | 'medium' | 'high';
  isCompleted: boolean;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
  photoEvidence?: string;
  estimatedDuration: number; // in minutes
  actualDuration?: number;
}

interface ChecklistData {
  date: string;
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  completionRate: number;
  totalTimeSpent: number;
}

const ModernDailyChecklist: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();
  const [checklistData, setChecklistData] = useState<ChecklistData>({
    date: format(new Date(), 'yyyy-MM-dd'),
    items: [],
    completedCount: 0,
    totalCount: 0,
    completionRate: 0,
    totalTimeSpent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    category: 'custom' as const,
    priority: 'medium' as const,
    estimatedDuration: 15,
  });
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const loadChecklistData = useCallback(async () => {
    setLoading(true);
    try {
      const currentDate = format(new Date(), 'yyyy-MM-dd');
      const items = await checklistService.getChecklistItems(currentDate);

      const completedCount = items.filter((item) => item.isCompleted).length;
      const totalCount = items.length;
      const completionRate =
        totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
      const totalTimeSpent = items
        .filter((item) => item.isCompleted && item.actualDuration)
        .reduce((total, item) => total + (item.actualDuration || 0), 0);

      setChecklistData({
        date: currentDate,
        items,
        completedCount,
        totalCount,
        completionRate,
        totalTimeSpent,
      });
    } catch (error) {
      console.error('Error loading checklist:', error);
      notify(
        translate(
          'pos.staff.checklist.failed_load',
          'Failed to load checklist data'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    loadChecklistData();
  }, [loadChecklistData]);

  const toggleItemCompletion = async (itemId: string) => {
    try {
      const item = checklistData.items.find((i) => i.id === itemId);
      if (!item) return;

      const isCompleted = !item.isCompleted;
      await checklistService.toggleItemCompletion(
        itemId,
        isCompleted,
        user?.email || String(user?.id || 'Current User'),
        item.notes
      );

      // Reload checklist data to get updated state
      await loadChecklistData();

      notify(
        translate(
          isCompleted
            ? 'pos.staff.checklist.item_completed'
            : 'pos.staff.checklist.item_uncompleted',
          isCompleted ? 'Item marked as completed' : 'Item marked as incomplete'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error toggling item completion:', error);
      notify(
        translate(
          'pos.staff.checklist.toggle_error',
          'Failed to update item status'
        ),
        { type: 'error' }
      );
    }
  };

  const addCustomItem = async () => {
    if (!newItem.title.trim()) {
      notify(
        translate(
          'pos.staff.checklist.add_title_required',
          'Please enter a title for the checklist item'
        ),
        { type: 'error' }
      );
      return;
    }

    try {
      await checklistService.createChecklistItem({
        title: newItem.title,
        description: newItem.description,
        category: newItem.category,
        priority: newItem.priority,
        isCompleted: false,
        estimatedDuration: newItem.estimatedDuration,
        date: checklistData.date,
        userId: user?.email || (user?.id ? String(user.id) : undefined),
      });

      setNewItem({
        title: '',
        description: '',
        category: 'custom',
        priority: 'medium',
        estimatedDuration: 15,
      });
      setShowAddItem(false);

      // Reload checklist data
      await loadChecklistData();

      notify(
        translate(
          'pos.staff.checklist.item_added',
          'Custom checklist item added'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error adding checklist item:', error);
      notify(
        translate(
          'pos.staff.checklist.add_error',
          'Failed to add checklist item'
        ),
        { type: 'error' }
      );
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'opening':
        return '🌅';
      case 'closing':
        return '🌙';
      case 'maintenance':
        return '🔧';
      case 'customer_service':
        return '👥';
      case 'custom':
        return '📝';
      default:
        return '📋';
    }
  };

  // Check if user has permission to edit/delete items
  const canEditDelete = () => {
    if (!user?.roles) return false;
    return (
      user.roles.includes('storeManager') || user.roles.includes('orgAdmin')
    );
  };

  const handleEditItem = (item: ChecklistItem) => {
    setEditingItem(item);
    setIsEditDialogOpen(true);
  };

  const handleDeleteItem = (itemId: string) => {
    setDeleteItemId(itemId);
    setIsDeleteDialogOpen(true);
  };

  const saveEditedItem = async () => {
    if (!editingItem) return;

    try {
      await checklistService.updateChecklistItem(editingItem.id, {
        title: editingItem.title,
        description: editingItem.description,
        category: editingItem.category,
        priority: editingItem.priority,
        estimatedDuration: editingItem.estimatedDuration,
        notes: editingItem.notes,
      });

      setEditingItem(null);
      setIsEditDialogOpen(false);

      // Reload checklist data
      await loadChecklistData();

      notify(
        translate(
          'pos.staff.checklist.item_updated',
          'Checklist item updated successfully!'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error updating checklist item:', error);
      notify(
        translate(
          'pos.staff.checklist.update_error',
          'Failed to update checklist item'
        ),
        { type: 'error' }
      );
    }
  };

  const confirmDeleteItem = async () => {
    if (!deleteItemId) return;

    try {
      await checklistService.deleteChecklistItem(deleteItemId);

      setDeleteItemId(null);
      setIsDeleteDialogOpen(false);

      // Reload checklist data
      await loadChecklistData();

      notify(
        translate(
          'pos.staff.checklist.item_deleted',
          'Checklist item deleted successfully!'
        ),
        { type: 'success' }
      );
    } catch (error) {
      console.error('Error deleting checklist item:', error);
      notify(
        translate(
          'pos.staff.checklist.delete_error',
          'Failed to delete checklist item'
        ),
        { type: 'error' }
      );
    }
  };

  const groupedItems = checklistData.items.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, ChecklistItem[]>);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">
            {translate('pos.staff.checklist.loading', 'Loading checklist...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">
          {translate('pos.staff.checklist.title', 'Daily Checklist')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {translate(
            'pos.staff.checklist.subtitle',
            'Your daily task checklist with completion tracking'
          )}
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate(
                    'pos.staff.checklist.stats_completed',
                    'Completed'
                  )}
                </p>
                <p className="text-2xl font-bold">
                  {checklistData.completedCount}/{checklistData.totalCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate('pos.staff.checklist.stats_progress', 'Progress')}
                </p>
                <p className="text-2xl font-bold">
                  {checklistData.completionRate.toFixed(0)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate(
                    'pos.staff.checklist.stats_time_spent',
                    'Time Spent'
                  )}
                </p>
                <p className="text-2xl font-bold">
                  {checklistData.totalTimeSpent}m
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {translate('pos.staff.checklist.stats_date', 'Date')}
                </p>
                <p className="text-lg font-semibold">
                  {new Date().toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Custom Item */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {translate(
                'pos.staff.checklist.add_custom_task',
                'Add Custom Task'
              )}
            </CardTitle>
            <Button
              variant="outline"
              onClick={() => setShowAddItem(!showAddItem)}
            >
              {showAddItem
                ? translate('pos.staff.checklist.cancel', 'Cancel')
                : translate('pos.staff.checklist.add_item', 'Add Item')}
            </Button>
          </div>
        </CardHeader>
        {showAddItem && (
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">
                  {translate('pos.staff.checklist.task_title', 'Task Title')}
                </Label>
                <Input
                  id="title"
                  value={newItem.title}
                  onChange={(e) =>
                    setNewItem((prev) => ({ ...prev, title: e.target.value }))
                  }
                  placeholder={translate(
                    'pos.staff.checklist.task_title_placeholder',
                    'Enter task title'
                  )}
                />
              </div>
              <div>
                <Label htmlFor="description">
                  {translate(
                    'pos.staff.checklist.description',
                    'Description (Optional)'
                  )}
                </Label>
                <Textarea
                  id="description"
                  value={newItem.description}
                  onChange={(e) =>
                    setNewItem((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder={translate(
                    'pos.staff.checklist.description_placeholder',
                    'Enter task description'
                  )}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">
                    {translate('pos.staff.checklist.category', 'Category')}
                  </Label>
                  <select
                    id="category"
                    value={newItem.category}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        category: e.target.value as any,
                      }))
                    }
                    className="w-full p-2 border rounded-md"
                    title={translate(
                      'pos.staff.checklist.category',
                      'Category'
                    )}
                  >
                    <option value="custom">
                      {translate(
                        'pos.staff.checklist.category_custom',
                        'Custom'
                      )}
                    </option>
                    <option value="opening">
                      {translate(
                        'pos.staff.checklist.category_opening',
                        'Opening'
                      )}
                    </option>
                    <option value="closing">
                      {translate(
                        'pos.staff.checklist.category_closing',
                        'Closing'
                      )}
                    </option>
                    <option value="maintenance">
                      {translate(
                        'pos.staff.checklist.category_maintenance',
                        'Maintenance'
                      )}
                    </option>
                    <option value="customer_service">
                      {translate(
                        'pos.staff.checklist.category_customer_service',
                        'Customer Service'
                      )}
                    </option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="priority">
                    {translate('pos.staff.checklist.priority', 'Priority')}
                  </Label>
                  <select
                    id="priority"
                    value={newItem.priority}
                    onChange={(e) =>
                      setNewItem((prev) => ({
                        ...prev,
                        priority: e.target.value as any,
                      }))
                    }
                    className="w-full p-2 border rounded-md"
                    title={translate(
                      'pos.staff.checklist.priority',
                      'Priority'
                    )}
                  >
                    <option value="low">
                      {translate('pos.staff.checklist.priority_low', 'Low')}
                    </option>
                    <option value="medium">
                      {translate(
                        'pos.staff.checklist.priority_medium',
                        'Medium'
                      )}
                    </option>
                    <option value="high">
                      {translate('pos.staff.checklist.priority_high', 'High')}
                    </option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="duration">
                  {translate(
                    'pos.staff.checklist.estimated_duration',
                    'Estimated Duration (minutes)'
                  )}
                </Label>
                <Input
                  id="duration"
                  type="number"
                  value={newItem.estimatedDuration}
                  onChange={(e) =>
                    setNewItem((prev) => ({
                      ...prev,
                      estimatedDuration: parseInt(e.target.value) || 15,
                    }))
                  }
                  min="1"
                  max="120"
                />
              </div>
              <Button onClick={addCustomItem} className="w-full">
                {translate('pos.staff.checklist.add_task_button', 'Add Task')}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Checklist Items by Category */}
      {Object.entries(groupedItems).map(([category, items]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">{getCategoryIcon(category)}</span>
              {category === 'custom'
                ? translate('pos.staff.checklist.category_custom', 'Custom')
                : category === 'opening'
                ? translate('pos.staff.checklist.category_opening', 'Opening')
                : category === 'closing'
                ? translate('pos.staff.checklist.category_closing', 'Closing')
                : category === 'maintenance'
                ? translate(
                    'pos.staff.checklist.category_maintenance',
                    'Maintenance'
                  )
                : translate(
                    'pos.staff.checklist.category_customer_service',
                    'Customer Service'
                  )}
              <Badge variant="secondary">
                {items.filter((item) => item.isCompleted).length}/{items.length}
              </Badge>
            </CardTitle>
            <CardDescription>
              {category === 'opening' &&
                translate(
                  'pos.staff.checklist.category_opening_desc',
                  'Tasks to complete when opening the store'
                )}
              {category === 'closing' &&
                translate(
                  'pos.staff.checklist.category_closing_desc',
                  'Tasks to complete when closing the store'
                )}
              {category === 'maintenance' &&
                translate(
                  'pos.staff.checklist.category_maintenance_desc',
                  'Regular maintenance and cleaning tasks'
                )}
              {category === 'customer_service' &&
                translate(
                  'pos.staff.checklist.category_customer_service_desc',
                  'Customer service and interaction tasks'
                )}
              {category === 'custom' &&
                translate(
                  'pos.staff.checklist.category_custom_desc',
                  'Your custom tasks and notes'
                )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-4 border rounded-lg"
                >
                  <Checkbox
                    checked={item.isCompleted}
                    onCheckedChange={() => toggleItemCompletion(item.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4
                        className={`font-medium ${
                          item.isCompleted
                            ? 'line-through text-muted-foreground'
                            : ''
                        }`}
                      >
                        {item.title}
                      </h4>
                      <Badge className={getPriorityColor(item.priority)}>
                        {item.priority === 'low'
                          ? translate('pos.staff.checklist.priority_low', 'Low')
                          : item.priority === 'medium'
                          ? translate(
                              'pos.staff.checklist.priority_medium',
                              'Medium'
                            )
                          : translate(
                              'pos.staff.checklist.priority_high',
                              'High'
                            )}
                      </Badge>
                      <Badge variant="outline">{item.estimatedDuration}m</Badge>
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {item.description}
                      </p>
                    )}
                    {item.isCompleted && (
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>
                          {translate(
                            'pos.staff.checklist.completed_label',
                            '✅ Completed'
                          )}
                        </span>
                        {item.completedAt && (
                          <span>
                            {translate(
                              'pos.staff.checklist.completed_at',
                              'at'
                            )}{' '}
                            {new Date(item.completedAt).toLocaleTimeString(
                              undefined,
                              { hour: '2-digit', minute: '2-digit' }
                            )}
                          </span>
                        )}
                        {item.actualDuration && (
                          <span>({item.actualDuration}m)</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {item.notes && (
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {item.photoEvidence && (
                      <Button variant="ghost" size="sm">
                        <Camera className="h-4 w-4" />
                      </Button>
                    )}
                    {canEditDelete() && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditItem(item)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Completion Summary */}
      {checklistData.completedCount === checklistData.totalCount &&
        checklistData.totalCount > 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-6 text-center">
              <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                {translate(
                  'pos.staff.checklist.all_completed_title',
                  '🎉 All Tasks Completed!'
                )}
              </h3>
              <p className="text-green-700">
                {translate(
                  'pos.staff.checklist.all_completed_message',
                  "Great job! You've completed all %{count} tasks in %{time} minutes."
                )
                  .replace('%{count}', `${checklistData.totalCount}`)
                  .replace('%{time}', `${checklistData.totalTimeSpent}`)}
              </p>
            </CardContent>
          </Card>
        )}

      {/* Edit Item Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {translate(
                'pos.staff.checklist.edit_title',
                'Edit Checklist Item'
              )}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.staff.checklist.edit_description',
                'Make changes to the checklist item below.'
              )}
            </DialogDescription>
          </DialogHeader>
          {editingItem && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-title" className="text-right">
                  {translate('pos.staff.checklist.task_title', 'Task Title')}
                </Label>
                <Input
                  id="edit-title"
                  value={editingItem.title}
                  onChange={(e) =>
                    setEditingItem({ ...editingItem, title: e.target.value })
                  }
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-description" className="text-right">
                  {translate(
                    'pos.staff.checklist.description',
                    'Description (Optional)'
                  )}
                </Label>
                <Textarea
                  id="edit-description"
                  value={editingItem.description || ''}
                  onChange={(e) =>
                    setEditingItem({
                      ...editingItem,
                      description: e.target.value,
                    })
                  }
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-duration" className="text-right">
                  {translate(
                    'pos.staff.checklist.edit_duration_label',
                    'Duration (min)'
                  )}
                </Label>
                <Input
                  id="edit-duration"
                  type="number"
                  value={editingItem.estimatedDuration}
                  onChange={(e) =>
                    setEditingItem({
                      ...editingItem,
                      estimatedDuration: parseInt(e.target.value) || 0,
                    })
                  }
                  className="col-span-3"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              {translate('pos.staff.checklist.cancel', 'Cancel')}
            </Button>
            <Button onClick={saveEditedItem}>
              {translate('pos.staff.checklist.save_changes', 'Save Changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.staff.checklist.delete_title', 'Are you sure?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.staff.checklist.delete_description',
                'This action cannot be undone. This will permanently delete the checklist item.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              {translate('pos.staff.checklist.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteItem}
              className="bg-red-600 hover:bg-red-700"
            >
              {translate('pos.staff.checklist.delete_button', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModernDailyChecklist;
